import { h } from "@stencil/core";
import { delegateFocus } from "@eventstore-ui/utils";
const PAGES_REF = '__PAGES_REF__';
/**
 * A multi step wizard. Each step can be targeted via a slot.
 * @slot [tabName] - Slots are created based off of the names of the passed pages.
 */
export class Wizard {
  constructor() {
    this.index = 0;
    this.elements = new Map();
    this.capture = (id) => (ref) => {
      if (ref) {
        this.elements.set(id, ref);
      }
      else {
        this.elements.delete(id);
      }
    };
    this.preventFocusShift = () => {
      if (!this.elements.has(PAGES_REF))
        return;
      this.elements.get(PAGES_REF).scrollTop = 0;
    };
    this.pages = undefined;
    this.location = undefined;
    this.scrollOffset = 0;
  }
  componentWillLoad() {
    this.updateIndex(this.location);
  }
  render() {
    return (h("div", { class: 'pages', ref: this.capture(PAGES_REF), onScroll: this.preventFocusShift }, this.pages.map(({ id }, i) => (h("section", { key: id, ref: this.capture(id), class: {
        page: true,
        active: id === this.location,
        passed: i < this.index,
        future: i > this.index,
      } }, h("slot", { name: id }))))));
  }
  updateIndex(location) {
    this.index = this.pages.findIndex(({ id }) => id === location);
  }
  animateHeight(newLocation) {
    const pages = this.elements.get(PAGES_REF);
    if (!pages)
      return;
    pages.classList.add('animating');
    this.updateIndex(newLocation);
    const currentHeight = pages.offsetHeight;
    const nextHeight = this.elements.get(newLocation)?.offsetHeight ?? 0;
    pages.style.setProperty('transition-property', 'none');
    pages.style.setProperty('min-height', `${currentHeight}px`);
    pages.addEventListener('transitionend', () => {
      pages.style.removeProperty('transition-property');
      pages.style.removeProperty('min-height');
      pages.classList.remove('animating');
    }, {
      once: true,
      passive: true,
    });
    requestAnimationFrame(() => {
      pages.style.setProperty('transition-property', 'min-height');
      pages.style.setProperty('min-height', `${nextHeight}px`);
      if (this.elements.has(newLocation)) {
        delegateFocus(this.elements.get(newLocation), {
          preventScroll: true,
        });
      }
      const top = this.host.getBoundingClientRect().top +
        window.scrollY +
        this.scrollOffset;
      window.scroll({
        behavior: 'smooth',
        top,
      });
    });
  }
  static get is() { return "es-wizard"; }
  static get encapsulation() { return "shadow"; }
  static get originalStyleUrls() {
    return {
      "$": ["es-wizard.css"]
    };
  }
  static get styleUrls() {
    return {
      "$": ["es-wizard.css"]
    };
  }
  static get properties() {
    return {
      "pages": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "WizardPage[]",
          "resolved": "WizardPage[]",
          "references": {
            "WizardPage": {
              "location": "import",
              "path": "./types",
              "id": "src/components/es-wizard/types.ts::WizardPage"
            }
          }
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "A list of pages describing each step."
        }
      },
      "location": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "The currently active page"
        },
        "attribute": "location",
        "reflect": false
      },
      "scrollOffset": {
        "type": "number",
        "mutable": false,
        "complexType": {
          "original": "number",
          "resolved": "number",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Offset the scroll to top on page change"
        },
        "attribute": "scroll-offset",
        "reflect": false,
        "defaultValue": "0"
      }
    };
  }
  static get elementRef() { return "host"; }
  static get watchers() {
    return [{
        "propName": "location",
        "methodName": "animateHeight"
      }];
  }
}
//# sourceMappingURL=es-wizard.js.map
